/**
 * Main layout component for Team Chat.
 * Provides the sidebar and main content area structure.
 */

import React, { useState } from 'react';
import { Box, Sheet, IconButton, Typography } from '@mui/joy';
import { List as MenuIcon } from '@phosphor-icons/react';
import ChatSidebar from './ChatSidebar';

interface ChatLayoutProps {
  children: React.ReactNode;
  activeChannelId?: string | null;
  onChannelSelect: (channelId: string) => void;
  onDMSelect: (userId: string) => void;
}

const ChatLayout: React.FC<ChatLayoutProps> = ({
  children,
  activeChannelId,
  onChannelSelect,
  onDMSelect,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  return (
    <Box
      sx={{
        display: 'flex',
        // Calculate height: viewport - top margin (84px) - footer (~52px)
        height: 'calc(100vh - 84px - 52px)',
        maxHeight: 'calc(100vh - 84px - 52px)',
        overflow: 'hidden',
      }}
    >
      {/* Desktop Sidebar */}
      <Sheet
        sx={{
          width: sidebarOpen ? 280 : 0,
          minWidth: sidebarOpen ? 280 : 0,
          transition: 'width 0.2s, min-width 0.2s',
          overflow: 'hidden',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
        }}
      >
        <ChatSidebar
          activeChannelId={activeChannelId}
          onChannelSelect={onChannelSelect}
          onDMSelect={onDMSelect}
          onClose={() => setSidebarOpen(false)}
        />
      </Sheet>

      {/* Mobile Sidebar (Drawer) */}
      <Sheet
        sx={{
          position: 'fixed',
          top: 84,
          left: 0,
          bottom: 52,
          width: 280,
          transform: mobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s',
          zIndex: 1200,
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
      >
        <ChatSidebar
          activeChannelId={activeChannelId}
          onChannelSelect={(id) => {
            onChannelSelect(id);
            setMobileSidebarOpen(false);
          }}
          onDMSelect={(id) => {
            onDMSelect(id);
            setMobileSidebarOpen(false);
          }}
          onClose={() => setMobileSidebarOpen(false)}
        />
      </Sheet>

      {/* Mobile Backdrop */}
      {mobileSidebarOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 84,
            left: 0,
            right: 0,
            bottom: 52,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1199,
            display: { xs: 'block', md: 'none' },
          }}
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        {/* Mobile Toggle Button */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            p: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton
            variant="plain"
            color="neutral"
            onClick={() => setMobileSidebarOpen(true)}
          >
            <MenuIcon />
          </IconButton>
          <Typography level="title-sm" sx={{ ml: 1 }}>
            Team Chat
          </Typography>
        </Box>

        {/* Desktop Toggle (when sidebar is closed) */}
        {!sidebarOpen && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              p: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <IconButton
              variant="plain"
              color="neutral"
              onClick={() => setSidebarOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        {/* Chat Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatLayout;
